import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { retry, tap } from 'rxjs/operators';

import { AuthService } from '@plugins/client/auth';
import { CountedList, Dict } from '@plugins/shared/types';
import { InstanceDto } from '@plugins/smartforms-lib/instance';
import {
  LookupLogDto,
  LookupLogFilters,
} from '@plugins/smartforms-lib/lookup-log';
import {
  SubmissionDto,
  SubmissionFilters,
} from '@plugins/smartforms-lib/submission';

import { JSON_FORMS_TENANT } from '../renderers';

/** Pagination plus all filters that are set. */
function listParams(
  skip: number | undefined,
  take: number | undefined,
  filters: object,
): HttpParams {
  let params = new HttpParams().append('skip', skip).append('take', take);
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params = params.append(key, String(value));
    }
  }
  return params;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly http = inject(HttpClient);
  private readonly tenant = inject(JSON_FORMS_TENANT);
  private readonly authService = inject(AuthService);

  private readonly _instance = signal<InstanceDto | null>(null);
  public readonly instance = this._instance.asReadonly();

  public readonly color = this.authService.userToken.theming_bg ?? '#e20074';

  fetchInstance(): Observable<InstanceDto> {
    let params = new HttpParams();
    if (this.tenant) {
      params = params.append('tenant', this.tenant);
    }
    return this.http.get<InstanceDto>('/api/instance', { params }).pipe(
      retry({ delay: 7000 }),
      tap((instance) => this._instance.set(instance)),
    );
  }

  updateInstance(instance: Partial<InstanceDto>): Observable<InstanceDto> {
    return this.http
      .patch<InstanceDto>('/api/instance', { instance })
      .pipe(tap((updated) => this._instance.set(updated)));
  }

  getFlowSubmissions({
    skip,
    take,
    filters = {},
  }: {
    skip?: number;
    take?: number;
    filters?: SubmissionFilters;
  }): Observable<CountedList<SubmissionDto>> {
    return this.http.get<CountedList<SubmissionDto>>('/api/submissions', {
      params: listParams(skip, take, filters),
    });
  }

  getLookupLogs({
    skip,
    take,
    filters,
  }: {
    skip: number;
    take: number;
    filters: LookupLogFilters;
  }): Observable<CountedList<LookupLogDto>> {
    return this.http.get<CountedList<LookupLogDto>>('/api/lookup-logs', {
      params: listParams(skip, take, filters),
    });
  }

  triggerFlow(ctx: Dict): Observable<void> {
    return this.http.post<void>('/api/submissions', ctx);
  }
}
